/**
 * DEMO-ONLY — thin HTTP handlers for sales-rep / affiliate switch + CSR co-browse.
 * Keeps mutable demo ops out of the large mock interceptor body (dias-reuse-and-handoff).
 */
import { HttpErrorResponse, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';

import { getAffiliatesForLogin } from './presidio-mock-discovery';
import { onPresidioDiscoveryAccountSwitch } from './presidio-mock-discovery-handlers';
import {
  clearPresidioMockImpersonationSession,
  getPresidioMockActiveLogin,
  isPresidioMockImpersonating,
  isPresidioMockSalesRepLogin,
  setPresidioMockActiveCustomerNo,
  setPresidioMockImpersonatedLogin,
} from './presidio-mock-session';

export function handlePresidioMockSalesRepCustomerSwitch(
  req: HttpRequest<unknown>,
  restPath: string,
  readJsonBody: <T>(body: unknown) => T
): Observable<HttpEvent<unknown>> | null {
  if (req.method.toUpperCase() !== 'POST' || !/^customers\/[^/]+\/users\/[^/]+\/switch$/i.test(restPath)) {
    return null;
  }
  const body = readJsonBody<{ customerNo?: string }>(req.body);
  const customerNo = (body?.customerNo || '').trim();
  const login = getPresidioMockActiveLogin();
  // Discovery users may only switch into affiliates assigned to them; sales rep keeps full demo set.
  if (!isPresidioMockSalesRepLogin(login)) {
    const allowed = getAffiliatesForLogin(login).map(a => a.customerNo);
    if (allowed.length && !allowed.includes(customerNo)) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 403,
            error: { error: 'affiliate account not assigned to this user' },
            url: req.url,
          })
      );
    }
  }
  if (!setPresidioMockActiveCustomerNo(customerNo)) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          error: { error: 'unknown affiliate customer' },
          url: req.url,
        })
    );
  }
  onPresidioDiscoveryAccountSwitch();
  return of(new HttpResponse({ status: 200, body: customerNo, url: req.url }));
}

export function handlePresidioMockCsrImpersonation(
  req: HttpRequest<unknown>,
  restPath: string,
  readJsonBody: <T>(body: unknown) => T
): Observable<HttpEvent<unknown>> | null {
  if (req.method.toUpperCase() !== 'POST') {
    return null;
  }
  if (/^customers\/[^/]+\/users\/[^/]+\/impersonate\/end$/i.test(restPath)) {
    clearPresidioMockImpersonationSession();
    return of(new HttpResponse({ status: 200, body: {}, url: req.url }));
  }
  if (!/^customers\/[^/]+\/users\/[^/]+\/impersonate$/i.test(restPath)) {
    return null;
  }
  const body = readJsonBody<{ login?: string }>(req.body);
  const login = (body?.login || '').trim();
  if (!setPresidioMockImpersonatedLogin(login)) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          error: { error: 'unknown impersonation target' },
          url: req.url,
        })
    );
  }
  return of(new HttpResponse({ status: 200, body: { login }, url: req.url }));
}

/** DEMO MVP 8.2.2: allow ATC while co-browsing; block order create / checkout only. */
export function rejectPresidioMockPurchaseWhileImpersonating(
  req: HttpRequest<unknown>,
  restPath: string
): Observable<HttpEvent<unknown>> | null {
  if (!isPresidioMockImpersonating()) {
    return null;
  }
  const method = req.method.toUpperCase();
  const isOrderCreate = method === 'POST' && (restPath === 'orders' || restPath.startsWith('orders?'));
  if (!isOrderCreate) {
    return null;
  }
  return throwError(
    () =>
      new HttpErrorResponse({
        status: 403,
        error: { error: 'Checkout is disabled while co-browsing as another user.' },
        url: req.url,
      })
  );
}
